import React, { useState, useContext, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom'

import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import Fab from '@mui/material/Fab'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'

import AddIcon from '@mui/icons-material/Add'
import ModeEditIcon from '@mui/icons-material/ModeEdit'

import Pagination from 'components/elements/Pagination'
import Search from 'components/forms/Search'

import { ThemeContext, UserContext } from 'components/common/App'

import rpc, { buildRequest, signoutAndRedirectToIndex } from 'lib/rpc'

export default function MascotsPage() {
  const theme = useContext(ThemeContext)
  // Creating/editing mascots is superuser-only (#290); staff get a read-only view.
  const currentUser = useContext(UserContext)
  const isSuperuser = currentUser && currentUser.role === "superuser"

  let { page = 1 } = useParams()
  const navigate = useNavigate()

  const [mascots, setMascots] = useState(null)
  const [perPage, setPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [searchString, setSearchString] = useState("")

  useEffect(() => {
    theme.changeTheme("light-bg")
    window.scrollTo(0, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setMascots(null)

    rpc.post(
      "",
      buildRequest(
        "admin.mascots.list",
        {
          page: page,
          per_page: perPage,
          search: searchString,
        },
      )
    ).then(response => {
      if (!response.data.result.message) {
        setMascots(response.data.result.data)
        setTotal(response.data.result.records)
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        console.error(response.data.result)
      }
    })
  }, [page, perPage, searchString])

  const setPage = (page) => {
    navigate(`/mascots/${page}`)
  }

  return (
    <>
      <Helmet>
        <title>Mascots</title>
      </Helmet>

      {isSuperuser && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => navigate("/mascots/create")}
        >
          <AddIcon />
        </Fab>
      )}

      <Container fixed>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="h3" gutterBottom>
                Mascots
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Paper padding={5}>
                <Search value={searchString} setSearchString={setSearchString} />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper>
                <TableContainer>
                  <Table size="small" stickyHeader aria-label="mascots table">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell align="left">Idle</TableCell>
                        <TableCell align="left">Slug</TableCell>
                        <TableCell align="left">Name</TableCell>
                        <TableCell align="left">Rarity</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell align="right">Owners</TableCell>
                        <TableCell align="right">Wishlist</TableCell>
                        <TableCell align="center">Active</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mascots ?
                        mascots.map((mascot) => (
                          <TableRow
                            key={mascot.id}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell component="th" scope="row">
                              <Link to={`/mascots/details/${mascot.id}`} component={RouterLink}>{mascot.id}</Link>
                            </TableCell>
                            <TableCell align="left">
                              {mascot.expressions && mascot.expressions.idle && (
                                <img src={mascot.expressions.idle} alt={mascot.slug} width="48" height="48" />
                              )}
                            </TableCell>
                            <TableCell align="left">
                              <Link to={`/mascots/details/${mascot.id}`} component={RouterLink}><ModeEditIcon /> {mascot.slug}</Link>
                            </TableCell>
                            <TableCell align="left">{mascot.name}</TableCell>
                            <TableCell align="left" sx={{ textTransform: 'capitalize' }}>{mascot.rarity}</TableCell>
                            <TableCell align="right">{mascot.cost}</TableCell>
                            <TableCell align="right">{mascot.owners_count}</TableCell>
                            <TableCell align="right">{mascot.wishlist_count}</TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={mascot.is_active ? "active" : "hidden"} color={mascot.is_active ? "success" : "default"} />
                            </TableCell>
                          </TableRow>
                        ))
                        :
                        <TableRow>
                          <TableCell colSpan={9}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      }
                    </TableBody>
                    <Pagination
                      total={total}
                      page={page}
                      perPage={perPage}
                      setPage={setPage}
                      setPerPage={setPerPage}
                    />
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container >
    </>
  )
}
